import crashlytics from "./crashlytics";
import { CURRENT_ENV } from "./api";
import { AxiosError, InternalAxiosRequestConfig } from "axios";

/**
 * Check if analytics should be enabled (production only)
 */
const isAnalyticsEnabled = (): boolean => {
  return CURRENT_ENV.includes("production");
};

/**
 * Enhanced response interceptor that logs API errors to Crashlytics
 * Add this to your axios response interceptors BEFORE your existing error handling
 *
 * @example
 * apiClient.interceptors.response.use(
 *   (response) => response,
 *   async (error) => {
 *     await enhancedResponseInterceptor(error).catch(() => {});
 *     // Your existing error handling here...
 *     return Promise.reject(error);
 *   }
 * );
 */
export const enhancedResponseInterceptor = async (error: AxiosError) => {
  if (!isAnalyticsEnabled()) {
    console.log("[Analytics Debug] API error (not logged):", error.message);
    return;
  }

  const endpoint = error.config?.url || "unknown";
  const method = error.config?.method?.toUpperCase() || "UNKNOWN";
  const status = error.response?.status || 0;
  const statusText = error.response?.statusText || "Unknown";
  const responseData = error.response?.data || {};

  console.error("[API Error]", {
    endpoint,
    method,
    status,
    statusText,
    data: responseData,
  });

  // Build error message
  const errorMessage = `API Error: ${status} ${method} ${endpoint}`;
  const errorObj = error instanceof Error ? error : new Error(errorMessage);

  // Report to Crashlytics with full context
  crashlytics.recordException(errorObj, {
    error_type: "api_error",
    api_endpoint: endpoint,
    api_method: method,
    api_status: String(status),
    api_status_text: statusText,
    api_response: JSON.stringify(responseData).substring(0, 500), // Truncate large responses
  });

  // Log specific error details as custom keys
  crashlytics.setCustomKey("last_api_error_endpoint", endpoint);
  crashlytics.setCustomKey("last_api_error_status", String(status));
  crashlytics.setCustomKey("last_api_error_method", method);

  // Log as breadcrumb for context in future errors
  crashlytics.log(`API Error: ${method} ${endpoint} - ${status}`);
};

/**
 * Enhanced request interceptor that tracks API request timing
 * Add this to your axios request interceptors
 *
 * @example
 * apiClient.interceptors.request.use(
 *   async (config) => {
 *     config = enhancedRequestInterceptor(config);
 *     // Your existing request handling here...
 *     return config;
 *   }
 * );
 */
export const enhancedRequestInterceptor = (
  config: InternalAxiosRequestConfig
): InternalAxiosRequestConfig => {
  // Add request timestamp for performance tracking
  (config as any).requestStartTime = Date.now();
  return config;
};

/**
 * Track slow API calls (>2 seconds)
 * Call this in your response interceptor's success handler
 *
 * @example
 * apiClient.interceptors.response.use(
 *   (response) => {
 *     trackSlowAPI(response);
 *     return response;
 *   },
 *   async (error) => {...}
 * );
 */
export const trackSlowAPI = (response: any) => {
  if (!isAnalyticsEnabled()) return;

  const requestStartTime = (response.config as any).requestStartTime;
  if (!requestStartTime) return;

  const duration = Date.now() - requestStartTime;
  const slowThreshold = 2000; // 2 seconds

  if (duration > slowThreshold) {
    const endpoint = response.config.url || "unknown";
    const method = response.config.method?.toUpperCase() || "UNKNOWN";

    console.warn("[Slow API]", {
      endpoint,
      method,
      duration: `${duration}ms`,
    });

    // Log slow API as non-critical issue
    crashlytics.log(`Slow API: ${method} ${endpoint} took ${duration}ms`);
    crashlytics.setCustomKey("last_slow_api", `${method} ${endpoint}`);
    crashlytics.setCustomKey("last_slow_api_duration", String(duration));
  }
};

/**
 * Optional: Track successful API calls for analytics
 * Use sparingly to avoid log spam
 *
 * @example
 * // Only track critical endpoints
 * if (endpoint.includes('/auth/login')) {
 *   trackSuccessfulAPICall(response);
 * }
 */
export const trackSuccessfulAPICall = (response: any) => {
  if (!isAnalyticsEnabled()) return;

  const endpoint = response.config.url || "unknown";
  const method = response.config.method?.toUpperCase() || "UNKNOWN";
  const status = response.status;

  crashlytics.log(`API Success: ${method} ${endpoint} - ${status}`);
};
